import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import {
  getConsentStatus,
  setConsentStatus,
  loadGA4,
  revokeGA4,
} from '../../lib/cookieConsent';
import { initPostHog } from '../../lib/posthog';
import { startSessionTimer } from '../../lib/services/analytics/posthogService';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show banner only if user hasn't made a choice yet
    if (getConsentStatus() === null) {
      setVisible(true);
    }
  }, []);

  const handleAccept = useCallback(() => {
    setConsentStatus('accepted');
    setVisible(false);

    // Initialize analytics now that consent is granted
    loadGA4();
    initPostHog();
    startSessionTimer();
  }, []);

  const handleReject = useCallback(() => {
    setConsentStatus('rejected');
    setVisible(false);
    revokeGA4();
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-orange-200 bg-white/95 shadow-lg backdrop-blur-sm dark:border-orange-900/40 dark:bg-gray-900/95">
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:gap-6 sm:p-5">
          {/* Icon + Text */}
          <div className="flex items-start gap-3 sm:flex-1">
            <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-phoenix-500" />
            <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
              We use cookies for analytics (Google Analytics, PostHog) to improve your experience.
              Essential cookies for authentication are always active.{' '}
              <Link
                to="/privacy"
                className="font-medium text-phoenix-600 underline underline-offset-2 hover:text-phoenix-700 dark:text-phoenix-400 dark:hover:text-phoenix-300"
              >
                Privacy Policy
              </Link>
            </p>
          </div>

          {/* Buttons */}
          <div className="flex flex-shrink-0 gap-3">
            <button
              onClick={handleReject}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Reject
            </button>
            <button
              onClick={handleAccept}
              className="rounded-lg bg-phoenix-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-phoenix-600"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
